import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

/**
 * VideoModal — luxe full-screen video player for the CADENCE site.
 *
 * Sophistication notes:
 *   - Eggplant velvet backdrop (no harsh black) with copper-rim chrome
 *   - Engraved gold frame around the <video> element
 *   - Close on ESC, on backdrop click, on X button
 *   - Body scroll lock while open
 *   - Autoplay with sound (browsers may block on first interaction —
 *     user already tapped a button to open this, so audio is allowed)
 *   - On close: video pauses + resets to 0 (no audio bleed)
 *   - Respects prefers-reduced-motion (no entrance scale animation)
 *
 * Props:
 *   - open:      boolean — whether the modal is visible
 *   - onClose:   () => void
 *   - src:       string — video URL (mp4)
 *   - title:     string — accessible title for screen readers
 *   - poster:    string (optional) — poster image while loading
 */
const VideoModal = ({ open, onClose, src, title, poster }) => {
  const videoRef = useRef(null);
  const dialogRef = useRef(null);

  // Body scroll lock + ESC close
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  // Autoplay on open; pause + reset on close so audio never lingers
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (open) {
      v.currentTime = 0;
      // Best-effort autoplay (the user just tapped to open this — should work)
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else {
      v.pause();
      try { v.currentTime = 0; } catch (_) { /* noop */ }
    }
  }, [open]);

  if (!open) return null;

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div
      ref={dialogRef}
      className="video-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title || 'CADENCE video'}
      onClick={handleBackdrop}
      data-testid="video-modal"
    >
      <div className="video-modal-frame" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="video-modal-close"
          onClick={onClose}
          aria-label="Close video"
          data-testid="video-modal-close"
        >
          <X size={22} />
        </button>
        <video
          ref={videoRef}
          className="video-modal-player"
          src={src}
          poster={poster}
          controls
          playsInline
          preload="metadata"
          data-testid="video-modal-player"
        />
        {title && (
          <p className="video-modal-caption" aria-hidden="true">{title}</p>
        )}
      </div>
    </div>
  );
};

export default VideoModal;
